/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

var _excluded = ["active", "className", "href", "icon", "itemComponent", "label", "linkComponent", "onClick", "onKeyDown", "to"];
var NavItem = function NavItem(_ref) {
  var active = _ref.active,
    className = _ref.className,
    href = _ref.href,
    Icon = _ref.icon,
    _ref$itemComponent = _ref.itemComponent,
    ItemComponent = _ref$itemComponent === void 0 ? 'li' : _ref$itemComponent,
    label = _ref.label,
    _ref$linkComponent = _ref.linkComponent,
    LinkComponent = _ref$linkComponent === void 0 ? 'a' : _ref$linkComponent,
    _onClick = _ref.onClick,
    _onKeyDown = _ref.onKeyDown,
    to = _ref.to,
    rest = _objectWithoutProperties(_ref, _excluded);
  var ref = useRef();
  var iconItem = !Icon ? null : /*#__PURE__*/React.createElement(Icon, {
    "aria-label": "icon",
    className: "cpc--side-nav__item-icon",
    "aria-hidden": "true"
  });
  return /*#__PURE__*/React.createElement(ItemComponent, {
    ref: ref,
    className: classnames('cpc--side-nav__item', {
      'cpc--side-nav__item--active': active
    }, className)
  }, /*#__PURE__*/React.createElement(LinkComponent, _extends({
    href: href,
    to: href ? undefined : to,
    className: "cpc--side-nav__link",
    onClick: function onClick(evt) {
      return _onClick(evt, href || to);
    },
    onKeyDown: function onKeyDown(evt) {
      return _onKeyDown(evt, href || to);
    },
    "aria-current": active ? 'page' : undefined
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, rest), iconItem, label));
};
NavItem.propTypes = {
  /**
   * Whether or not the page the user is representative of the currently selected nav item.
   */
  active: PropTypes.bool,
  /**
   * The class name to apply to the nav item's li element.
   */
  className: PropTypes.string,
  /**
   * The location to link the nav item to.
   */
  href: PropTypes.string,
  /**
   * Icon of the item
   */
  icon: PropTypes.shape({}),
  /**
   * A component or element to use for the item element.
   */
  itemComponent: PropTypes.elementType,
  /**
   * The value of the nav item to be rendered.
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * A component or element to use for the link element. If you're using something like React Router this can be a helpful backdoor to pass
   * in your own component, so the page doesn't re-render.
   */
  linkComponent: PropTypes.elementType,
  /**
   * A call back function to fire upon clicking the nav item.
   */
  onClick: PropTypes.func.isRequired,
  /**
   * A call back function to fire upon pressing a key on the nav item.
   */
  onKeyDown: PropTypes.func.isRequired,
  /**
   * The location to link the nav item to if using a Link Component.
   */
  to: PropTypes.string
};
var NavItem$1 = NavItem;

export { NavItem$1 as default };
